import { Box } from "@mui/material";
import Link from "next/link";
import { useTranslation } from "react-i18next";
import { SITE_BUTTON_STYLES } from "../siteKit/siteButtonStyles";
import type { NewsArticle, NewsContentBlock } from "./newsArticles";
import { NewsImage } from "./NewsImage";
import { NewsRelatedLinks } from "./NewsRelatedLinks";
import { NEWS_CAPTION_STYLES, NEWS_DETAIL_STYLES } from "./newsStyles";

export interface NewsArticleDetailProps {
  article: NewsArticle;
  /** 게재일 표시 문자열(호출부가 언어에 맞춰 만든다) */
  formattedDate: string;
}

/** 기자 메일 한 줄(시안 `.mail`) — 원문 마지막 문단이 주소 하나뿐이면 옅게 보인다 */
const MAIL_ONLY = /^\S+@\S+\.\S+$/;
const BACK_PATH = "M11 4L6 9l5 5";
/** 본문 사진이 화면에 보이는 폭 — 본문 칸(712px)을 넘지 않는다 */
const DETAIL_IMAGE_SIZES = "(max-width:760px) 100vw, 712px";

interface NewsDetailBodyProps {
  /** 원문 콘텐츠 블록 — 배열 순서가 곧 화면 순서다 */
  content: NewsContentBlock[];
  /** 문단 블록이 하나도 없을 때 본문 자리에 대신 보일 요약문 */
  summary: string;
  /** `alt` 가 없는 사진의 접근성 이름·대체 글자(기사 제목) */
  imageTextFallback: string;
}

/** 원문 순서대로 사진·소제목·문단·목록을 옮긴다. 읽을 문단이 없으면 요약문을 뒤에 덧붙인다(press-news). */
function NewsDetailBody({ content, summary, imageTextFallback }: NewsDetailBodyProps) {
  // "원문에 본문 문단이 없으면" 은 content 가 비었는지가 아니라 문단 블록이 있는지로 판정한다(사진 기사).
  const hasParagraph = content.some((block) => block.type === "paragraph");
  // 첫 사진은 제목 바로 아래 첫 화면에 보인다 — 페이지를 열자마자 받는다
  const firstImageIndex = content.findIndex((block) => block.type === "image");
  return (
    <div>
      {content.map((block, index) => {
        if (block.type === "image") {
          // 빈 alt 는 부재로 본다(`||`) — 본문 사진이 보조 기술에서 지워지거나 실패 시 빈 칸만 남지 않게.
          const imageText = block.alt || imageTextFallback;
          return (
            <Box component="figure" key={`image-${index}`} sx={NEWS_DETAIL_STYLES.figure}>
              <NewsImage
                src={block.src}
                srcSet={block.srcSet}
                sizes={DETAIL_IMAGE_SIZES}
                alt={imageText}
                fallbackText={imageText}
                width={block.width}
                height={block.height}
                priority={index === firstImageIndex}
              />
              {block.alt ? (
                <Box component="figcaption" sx={NEWS_CAPTION_STYLES}>
                  {block.alt}
                </Box>
              ) : null}
            </Box>
          );
        }
        if (block.type === "heading") {
          return (
            <Box component="h2" key={`heading-${index}`} sx={NEWS_DETAIL_STYLES.heading}>
              {block.text}
            </Box>
          );
        }
        if (block.type === "list") {
          return (
            <Box component="ul" key={`list-${index}`} sx={NEWS_DETAIL_STYLES.list}>
              {block.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </Box>
          );
        }
        return (
          <Box
            component="p"
            key={`paragraph-${index}`}
            sx={MAIL_ONLY.test(block.text) ? NEWS_DETAIL_STYLES.mail : NEWS_DETAIL_STYLES.paragraph}
          >
            {block.text}
          </Box>
        );
      })}
      {hasParagraph ? null : (
        <Box component="p" sx={NEWS_DETAIL_STYLES.paragraph}>
          {summary}
        </Box>
      )}
    </div>
  );
}

/**
 * 언론보도 상세(`/news/[id]`, press-news): 「목록으로」, 제목, 출처·기자·날짜(아래 구분선), 원문 블록, (번역본이면) 번역 안내,
 * 기사 끝 「원문보기」(새 탭)·「목록으로」, 기사 아래 「함께 보면 좋아요」.
 * 외부로 나가는 길은 「원문보기」 하나다 — 「함께 보면 좋아요」는 사이트 안 이동이다.
 */
export function NewsArticleDetail({ article, formattedDate }: NewsArticleDetailProps) {
  const { t } = useTranslation("landing");
  const handleOriginalClick = () => {
    window.open(article.originalUrl, "_blank", "noopener,noreferrer");
  };
  return (
    <>
      <Box component="article" sx={NEWS_DETAIL_STYLES.article}>
        <Box component={Link} href="/news" sx={NEWS_DETAIL_STYLES.back}>
          <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
            <path d={BACK_PATH} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {t("site.news.back")}
        </Box>
        <Box component="h1" sx={NEWS_DETAIL_STYLES.title}>
          {article.title}
        </Box>
        <Box component="p" sx={NEWS_DETAIL_STYLES.meta}>
          {article.source}
          {article.author ? ` · ${article.author}` : ""} · {formattedDate}
        </Box>
        <NewsDetailBody content={article.content} summary={article.summary} imageTextFallback={article.title} />
        {article.translatedFrom ? (
          <Box component="p" sx={NEWS_DETAIL_STYLES.translated}>
            {t(`site.news.translatedFrom.${article.translatedFrom}`)}
          </Box>
        ) : null}
        <Box sx={NEWS_DETAIL_STYLES.actions}>
          <Box component="button" type="button" onClick={handleOriginalClick} sx={SITE_BUTTON_STYLES.primaryLarge}>
            {t("site.news.original")}
          </Box>
          <Box component={Link} href="/news" sx={SITE_BUTTON_STYLES.greyLarge}>
            {t("site.news.back")}
          </Box>
        </Box>
      </Box>
      <NewsRelatedLinks />
    </>
  );
}
